import {
    BoolFieldElement,
    DoubleFieldElement,
    DoubleValidator,
    FieldElement,
    IntFieldElement,
    IntValidator,
    RangeBoundaryDouble,
    RangeBoundaryInt64,
    StringFieldElement,
    StringValidator,
} from '@kcftech/prometheustypescriptapiclient';
import {
    AssemblyNumberParameterData,
    AssemblyParameterFormData,
    AssemblyParameterNumberFormData,
    TypedAssemblyParameterFormData,
} from 'pages/Settings/AssemblyType/parameter_form/types';
import { RegisterOptions } from 'react-hook-form';
import { parameterTypeToInputMode } from 'utils/assembly_application';
import { isValidInteger, isValidNumber } from 'utils/number';
import { undefinedIfEmpty } from 'utils/string';

export const defaultValueMaxLength = 300;
export const validValueMaxLength = 300;

export const stringDefaultValueRules: RegisterOptions<
    TypedAssemblyParameterFormData<'selection' | 'text'>,
    'defaultValue'
> = {
    maxLength: {
        value: defaultValueMaxLength,
        message: `Default Value cannot exceed ${defaultValueMaxLength} characters`,
    },
    validate: () => undefined,
};

type NumericValueType = 'integer' | 'decimal';

const isValidNumericValue = (
    value: string | number | undefined,
    type: NumericValueType
): value is string | number =>
    type === 'decimal' ? isValidNumber(value) : isValidInteger(value);

function getBoundaryNumber(
    value: number | undefined,
    type: NumericValueType
): number | undefined {
    if (value === undefined) {
        return undefined;
    }

    if (!isValidNumber(value)) {
        throw new Error('boundary number is not a valid number');
    }
    const valueAsNumber = Number(value);
    if (type === 'integer' && !Number.isSafeInteger(valueAsNumber)) {
        throw new Error('boundary number for int field was not an integer');
    }
    return valueAsNumber;
}

export type GetNumericValueRulesOptions = Readonly<{
    type: NumericValueType;
    fieldName: string;
}> &
    AssemblyNumberParameterData;

export const getNumericValueRules = ({
    type,
    fieldName,
    minValue,
    maxValue,
    minValueInclusive,
    maxValueInclusive,
}: GetNumericValueRulesOptions): RegisterOptions<
    AssemblyParameterNumberFormData,
    'defaultValue'
> => ({
    setValueAs: (v) =>
        v !== undefined && ((typeof v === 'string' && v.trim()) || typeof v === 'number')
            ? Number(v)
            : undefined,
    validate: (value) => {
        if (value === undefined || value.toString() === '') {
            return undefined;
        }

        if (typeof value === 'boolean') {
            throw new Error('expected numeric type, received boolean');
        }

        if (!isValidNumericValue(value, type)) {
            return type === 'decimal'
                ? `${fieldName} must be a valid number`
                : `${fieldName} must be a valid integer`;
        }

        const parsedValue = Number(value);

        const minBoundary = getBoundaryNumber(minValue, type);
        const isMinValueInclusive = type === 'integer' || minValueInclusive;

        if (minBoundary !== undefined) {
            if (isMinValueInclusive && parsedValue < minBoundary) {
                return `${fieldName} must be greater than or equal to Min Value: ${minBoundary}`;
            }
            if (!isMinValueInclusive && parsedValue <= minBoundary) {
                return `${fieldName} must be greater than Min Value: ${minBoundary}`;
            }
        }

        const maxBoundary = getBoundaryNumber(maxValue, type);
        const isMaxValueInclusive = type === 'integer' || maxValueInclusive;

        if (maxBoundary !== undefined) {
            if (isMaxValueInclusive && parsedValue > maxBoundary) {
                return `${fieldName} must be less than or equal to Max Value: ${maxBoundary}`;
            }
            if (!isMaxValueInclusive && parsedValue >= maxBoundary) {
                return `${fieldName} must be less than Max Value: ${maxBoundary}`;
            }
        }

        return undefined;
    },
});

export const getMinValueRules = (
    type: NumericValueType,
    maxValue: number | undefined
): RegisterOptions<AssemblyParameterFormData, 'minValue'> => ({
    setValueAs: (v) =>
        v !== undefined && ((typeof v === 'string' && v.trim()) || typeof v === 'number')
            ? Number(v)
            : undefined,
    validate: (value) => {
        if (value === undefined) {
            return undefined;
        }

        if (!isValidNumericValue(value, type)) {
            return type === 'integer'
                ? 'Min Value must be a valid integer'
                : 'Min Value must be a valid number';
        }

        if (maxValue !== undefined && value > maxValue) {
            return 'Min Value must be less than or equal to Max Value';
        }

        return undefined;
    },
});

export const getMaxValueRules = (
    type: NumericValueType,
    minValue: number | undefined
): RegisterOptions<AssemblyParameterFormData, 'maxValue'> => ({
    setValueAs: (v) =>
        v !== undefined && ((typeof v === 'string' && v.trim()) || typeof v === 'number')
            ? Number(v)
            : undefined,
    validate: (value) => {
        if (value === undefined) {
            return undefined;
        }

        if (!isValidNumericValue(value, type)) {
            return type === 'integer'
                ? 'Max Value must be a valid integer'
                : 'Max Value must be a valid number';
        }

        if (minValue !== undefined && value < minValue) {
            return 'Max Value must be greater than or equal to Min Value';
        }

        return undefined;
    },
});

export const getAssemblyFieldElementRequest = (
    formData: AssemblyParameterFormData
): FieldElement => {
    const baseInfo = {
        FieldKey: formData.code,
        FieldDisplayName: formData.name,
        InputMode: parameterTypeToInputMode[formData.type],
        Validator: undefined,
        UnitDisplayName: undefined,
    };

    switch (formData.type) {
        case 'bool':
            return new BoolFieldElement({
                ...baseInfo,
                DefaultValue:
                    typeof formData.defaultValue === 'boolean'
                        ? formData.defaultValue
                        : undefined,
            });
        case 'text':
            return new StringFieldElement({
                ...baseInfo,
                DefaultValue: undefinedIfEmpty(formData.defaultValue),
            });
        case 'selection':
            return new StringFieldElement({
                ...baseInfo,
                DefaultValue: undefinedIfEmpty(formData.defaultValue),
                Validator: new StringValidator({
                    ValidValues: formData.validValues?.map((val) => val.toString()),
                    ValidationRegex: undefined,
                }),
            });
        case 'decimal':
            return new DoubleFieldElement({
                ...baseInfo,
                DefaultValue: formData.defaultValue,
                Validator: new DoubleValidator({
                    Min:
                        formData.minValue !== undefined
                            ? new RangeBoundaryDouble({
                                  Value: formData.minValue,
                                  Exclusive:
                                      formData.minValueInclusive !== undefined
                                          ? !formData.minValueInclusive
                                          : false,
                              })
                            : undefined,
                    Max:
                        formData.maxValue !== undefined
                            ? new RangeBoundaryDouble({
                                  Value: formData.maxValue,
                                  Exclusive:
                                      formData.maxValueInclusive !== undefined
                                          ? !formData.maxValueInclusive
                                          : false,
                              })
                            : undefined,
                }),
            });
        case 'integer':
            return new IntFieldElement({
                ...baseInfo,
                DefaultValue: formData.defaultValue,
                Validator: new IntValidator({
                    Min:
                        formData.minValue !== undefined
                            ? new RangeBoundaryInt64({
                                  Value: formData.minValue,
                                  Exclusive: false,
                              })
                            : undefined,
                    Max:
                        formData.maxValue !== undefined
                            ? new RangeBoundaryInt64({
                                  Value: formData.maxValue,
                                  Exclusive: false,
                              })
                            : undefined,
                }),
            });
        default:
            throw new Error(`Invalid field element type in form data: ${formData}`);
    }
};
